import React, { useCallback, useState, useEffect } from 'react';
import { Dialog, Loading } from '@koopajs/mui';
import { useResourceShow, usePermissions } from '@koopajs/react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { IUploadedMeeting, IDocument } from 'types';
import { EventsTimeline } from 'components/EventsTimeline';
import { useCommitteeName } from 'components/hooks/useCommitteeName';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { formatDate } from 'utils/DateTime/formatDate';
import { useLocale } from '@koopajs/react';
import { RenderDocumentIcon } from 'components/RenderDocumentIcon';
import prettyBytes from 'pretty-bytes';
import { DocumentList } from 'components/DocumentList';
import DownloadIcon from '@mui/icons-material/Download';
import { useAppSnackbar } from 'components/hooks/useAppSnackbar';
import DownloadingIcon from '@mui/icons-material/Downloading';
import { Edit as EditIcon } from '@mui/icons-material';
import { UploadedMeetingDialogEditForm } from 'components/UploadedMeeting/UploadedMeetingDialogEditForm';
import { DeleteUploadedMeeting } from 'components/Modals/DeleteUploadedMeeting';
interface IViewUploadedMeeting {
  uploadedMeetingId: string;
}

export const ViewUploadedMeeting: React.FC<IViewUploadedMeeting> = (props) => {
  const { uploadedMeetingId } = props;

  const [isInEditMode, setIsInEditMode] = useState(false);
  const [isDeleteUploadedMeetingOpen, setIsDeleteUploadedMeetingOpen] = useState(false);

  const history = useHistory();

  const { resource: uploadedMeeting, errorMessage } = useResourceShow<IUploadedMeeting>({
    path: '/uploaded-meetings',
    id: uploadedMeetingId
  });

  const { resource: mainDocument, isProcessing: isDocumentProcessing } = useResourceShow<IDocument>({
    path: '/documents',
    id: uploadedMeeting?.mainDocumentId
  });

  const { t, locale } = useLocale();

  const hasDocuments = Boolean(uploadedMeeting?.documentsIds?.length);

  const extractedErrorMessage = extractErrorMessage(errorMessage);
  const committeeType = useCommitteeName(uploadedMeeting?.committeeId || '');
  const keyPrefix = 'Dialogs.ViewUploadedMeeting';

  const isUserWorkspaceOwner = usePermissions({ requiredPermissions: 'owner' });

  const timelineEvents = [
    {
      title: t('Components.EventsTimeline.updatedOn') || '',
      timestamp: uploadedMeeting?.$updatedAt || ''
    },
    {
      title: t('Components.EventsTimeline.createdOn') || '',
      timestamp: uploadedMeeting?.$createdAt || '',
      userId: uploadedMeeting?.$ownedBy || ''
    }
  ];

  const isPdfOrImage =
    mainDocument?.attachedFile?.format === 'application/pdf' ||
    mainDocument?.attachedFile?.format.startsWith('image');

  const handleCloseDialog = useCallback(() => {
    history.push({ pathname: '/past-meetings', search: history.location?.search });
    setIsInEditMode(false);
  }, [JSON.stringify(history)]);

  const { triggerSnackbar } = useAppSnackbar();

  useEffect(() => {
    if (errorMessage) {
      triggerSnackbar({
        snackbarText: extractedErrorMessage,
        variant: 'error'
      });

      handleCloseDialog();
    }
  }, [errorMessage, uploadedMeetingId]);

  const handleSetToEditMode = useCallback(() => {
    return () => setIsInEditMode(true);
  }, []);

  const handleOpenDeleteModal = useCallback(() => {
    setIsDeleteUploadedMeetingOpen(true);
  }, []);
  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteUploadedMeetingOpen(false);
  }, []);

  return (
    <Dialog.View
      isOpen={Boolean(uploadedMeetingId && uploadedMeeting)}
      onClose={handleCloseDialog}
      isCloseVisible={true}
      i18n={{ keyPrefix: keyPrefix + '.DialogView' }}
      footerActions={
        <>
          {mainDocument?.attachedFile && !isDocumentProcessing && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              href={`/documents/${mainDocument?.id}`}
              target="_blank"
              rel="noopener"
              data-cy=""
            >
              {t(keyPrefix + '.DialogView.downloadButtonLabel')}
            </Button>
          )}
        </>
      }
      dialogProps={{ className: 'rr-block', PaperProps: { 'data-cy': 'uploaded-meeting-dialog' } }}
    >
      {isDocumentProcessing ? (
        <Box sx={{ my: 3 }}>
          <Loading sx={{ backgroundColor: 'transparent' }} />
        </Box>
      ) : (
        uploadedMeeting &&
        mainDocument?.attachedFile &&
        !isDocumentProcessing && (
          <>
            <Box sx={{ mb: 6.25, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {isPdfOrImage ? (
                <Box>
                  <iframe
                    style={{
                      border: '1px solid gray',
                      backgroundColor: 'rgba(0, 0, 0, 0.11)',
                      width: '100%',
                      height: 'calc(100vh - 64px - 64px - 52.5px)'
                    }}
                    className="rr-block"
                    src={mainDocument?.attachedFileUri}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: '24px',
                    borderRadius: '4px',
                    border: '1px solid #dddddd',
                    gap: 2
                  }}
                >
                  <DownloadingIcon />
                  <Typography align="center" variant="h5">
                    {t(keyPrefix + '.downloadDocumentTitle')}
                  </Typography>

                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    href={`/documents/${mainDocument?.id}`}
                    target="_blank"
                    rel="noopener"
                  >
                    {t(keyPrefix + '.downloadButtonLabel')}
                  </Button>
                </Box>
              )}

              {isInEditMode && mainDocument ? (
                <UploadedMeetingDialogEditForm
                  uploadedMeeting={uploadedMeeting}
                  mainDocument={mainDocument}
                  setIsInEditMode={setIsInEditMode}
                />
              ) : (
                <Box
                  sx={{
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    borderRadius: '4px',
                    border: '1px solid #dddddd'
                  }}
                  data-cy="uploaded-meeting-dialog_info-card"
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography
                      variant="h6"
                      sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', md: 'row' } }}
                    >
                      <span>{t(keyPrefix + '.agendaLabel')}</span>
                      <Box component="span" sx={{ display: { xs: 'none', md: 'flex' } }}>
                        |
                      </Box>
                      <Box component="span" data-cy="uploaded-meeting-dialog_info-card_committee">
                        {committeeType}
                      </Box>
                    </Typography>
                    {!isInEditMode && isUserWorkspaceOwner && (
                      <IconButton
                        sx={{ alignSelf: 'flex-start', ml: 2 }}
                        onClick={handleSetToEditMode()}
                        data-cy="uploaded-meeting-dialog_edit-button"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Typography variant="body1" data-cy="uploaded-meeting-dialog_info-card_date">
                    {formatDate({
                      isoString: uploadedMeeting.startDateTime,
                      format: 'DATE_HUGE',
                      locale,
                      isUTC: true
                    })}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      opacity: '0.6',
                      alignItems: { xs: 'flex-start', md: 'center' },
                      flexWrap: 'wrap',
                      flexDirection: { xs: 'column', md: 'row' },
                      overflow: 'hidden'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      }}
                      data-cy="uploaded-meeting-dialog_info-card_title"
                    >
                      {uploadedMeeting.title}
                    </Typography>
                    {mainDocument?.attachedFile && (
                      <>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'flex' } }}>
                          &#183;
                        </Box>
                        <RenderDocumentIcon fileType={mainDocument?.attachedFile?.format} />
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            minWidth: 0
                          }}
                        >
                          {mainDocument?.attachedFile.name}
                        </Typography>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'flex' } }}>
                          &#183;
                        </Box>
                        <Typography variant="body2">
                          {prettyBytes(mainDocument?.attachedFile.sizeBytes)}
                        </Typography>
                      </>
                    )}
                  </Box>
                  {hasDocuments && (
                    <DocumentList
                      path={`/uploaded-meetings/${uploadedMeeting.id}/documents`}
                      isAccordion={true}
                      accordionOptions={{
                        defaultExpanded: true,
                        label: t(keyPrefix + '.documentationLabel'),
                        documentsVisible: 2
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
            <EventsTimeline timelineEvents={timelineEvents} />
            {isUserWorkspaceOwner && (
              <>
                <DeleteUploadedMeeting
                  uploadedMeeting={uploadedMeeting}
                  isOpen={isDeleteUploadedMeetingOpen}
                  onClose={handleCloseDeleteModal}
                />
                <Button
                  onClick={handleOpenDeleteModal}
                  sx={{ color: 'red', display: { xs: 'none', sm: 'block' } }}
                  data-cy="uploaded-meeting-dialog_delete-button"
                >
                  {t(keyPrefix + '.labelDelete')}
                </Button>
              </>
            )}
          </>
        )
      )}
    </Dialog.View>
  );
};
